// Design tokens for the analytics one-pager. Kept apart from the other style modules because these
// are *chart* tokens: the categorical slots below were validated as a set, and editing one hex in
// isolation silently breaks that validation.
//
// The categorical trio was chosen by running the palette validator, not by eye, against the sheet's
// own white surface (#FFFFFF) with every pair in play:
//
//   #4F8506 / #EDA100 / #4A3AA7  → all checks pass
//     worst CVD pair      gold↔green  ΔE 14.7 (protanopia), 14.6 (tritanopia)   [>= 8 required]
//     worst normal pair   gold↔green  ΔE 25.6                                    [>= 15 required]
//
// Two findings worth keeping, because both are counter-intuitive:
//   1. The brand green cannot sit beside a *darker* gold. #C97A00 measures ΔE 2.6 against the green
//      under protanopia and #B87A00 measures 1.4 — indistinguishable. The bright #EDA100 works
//      precisely because the separation is carried by lightness rather than hue.
//   2. #EDA100 costs a contrast WARN on white (2.17:1, under the 3:1 bar). That is not dismissable:
//      it obligates "relief" — every value it encodes must also be readable as text. The integrator
//      section therefore prints the numbers for all three groups next to each bar, so the gold never
//      carries a value on its own.
//
// The app is light-only (there is no dark theme anywhere in this codebase) and this sheet is built to
// be printed on white, so a dark variant is deliberately not defined rather than machine-flipped.

/** Categorical slots — identity, assigned to a fixed group. Never reassign by rank or size. */
export const personaGroupColors = {
  farmers: '#4F8506',
  agents: '#EDA100',
  orgUsers: '#4A3AA7',
};

/** Slot 1 doubles as the single-series colour for the persona and trend charts. */
export const seriesPrimary = personaGroupColors.farmers;

/** Chart chrome and ink. Text never wears a series colour — identity comes from the mark beside it. */
export const vizInk = {
  surface: '#FFFFFF',
  plane: '#F9F9F7',
  primary: '#0B0B0B',
  secondary: '#52514E',
  muted: '#898781',
  gridline: '#E1E0D9',
  baseline: '#C3C2B7',
  hairline: 'rgba(11, 11, 11, 0.10)',
};

/** Mark specs from the visualization guidance, in one place so every chart obeys the same ones. */
export const markSpecs = {
  barMaxThickness: 24,
  barEndRadius: 4,
  lineWidth: 2,
  markerRadius: 4,
  surfaceGap: 2,
  areaFillOpacity: 0.1,
};

export const personaGroupLegend = [
  { key: 'farmers', label: 'Farmers', color: personaGroupColors.farmers },
  { key: 'agents', label: 'Agents', color: personaGroupColors.agents },
  { key: 'orgUsers', label: 'Organisation users', color: personaGroupColors.orgUsers },
];

/**
 * Compact form for the stat tiles only. Proportional figures are used everywhere a number stands
 * alone — `tabular-nums` gives every digit the width of a zero, which makes a display-size value
 * like 121 look loose. Table columns opt into tabular-nums instead, where alignment is the point.
 */
export const formatCompact = (value) => {
  const n = Number(value) || 0;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${(n / 1000).toFixed(0)}K`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

export const formatFull = (value) => (Number(value) || 0).toLocaleString();

export const cardSx = {
  backgroundColor: vizInk.surface,
  border: `1px solid ${vizInk.hairline}`,
  borderRadius: '14px',
  padding: '18px 20px',
};

export const cardTitleSx = {
  fontSize: 11,
  lineHeight: '16px',
  letterSpacing: '0.8px',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: vizInk.muted,
  marginBottom: '14px',
};
